import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMarket, useResolveMarket } from '../hooks/useMarkets';
import { formatTokens } from '../lib/format';
import { getErrorMessage } from '../services/api-client';
import { useAuthStore } from '../stores/authStore';
import { colors, mono } from '../themes/theme';

/**
 * The one screen for saying what happened.
 *
 * Deliberately separate from the market view: everyone reads that one, and only
 * one person acts here. Mixing them put a payout control in front of people who
 * cannot use it and buried it for the person who can.
 */
export function Settle() {
    const { marketId } = useParams<{ marketId: string }>();
    const id = Number(marketId);
    const navigate = useNavigate();
    const session = useAuthStore((s) => s.session);

    const market = useMarket(Number.isInteger(id) && id > 0 ? id : undefined);
    const resolve = useResolveMarket();

    const [pick, setPick] = useState<number | null>(null);
    const [value, setValue] = useState('');
    const [confirming, setConfirming] = useState(false);

    if (market.isPending) return <Typography color="text.secondary">Loading…</Typography>;
    if (market.isError) return <Alert severity="error">{getErrorMessage(market.error)}</Alert>;

    const m = market.data;
    const needsNumber = m.kind === 'closest' || m.kind === 'over_under';
    const chosen = m.outcomes.find((o) => o.id === pick);

    if (session?.id !== m.resolverId) {
        return (
            <Stack spacing={2}>
                <Typography variant="h2">Not your call</Typography>
                <Typography color="text.secondary">
                    {m.question} — someone else decides this one.
                </Typography>
                <Button variant="contained" onClick={() => navigate(`/markets/${m.id}`)}>
                    Look at the bet
                </Button>
            </Stack>
        );
    }

    if (m.status === 'settled' || m.status === 'void') {
        return (
            <Stack spacing={2}>
                <Typography variant="h2">Already done</Typography>
                <Typography color="text.secondary">{m.question}</Typography>
                <Button variant="contained" onClick={() => navigate(`/markets/${m.id}`)}>
                    See how it went
                </Button>
            </Stack>
        );
    }

    const ready = needsNumber ? value.trim() !== '' : pick !== null;

    return (
        <Stack spacing={2.5}>
            <Button onClick={() => navigate('/')} color="inherit" sx={{ alignSelf: 'flex-start', px: 0 }}>
                ← Board
            </Button>

            <Box>
                <Typography variant="overline" sx={{ color: colors.safety }}>
                    What happened?
                </Typography>
                <Typography variant="h3" sx={{ textTransform: 'none', fontSize: '1.5rem' }}>
                    {m.question}
                </Typography>
                <Typography sx={{ fontFamily: mono, fontSize: '0.8rem', color: colors.muted, mt: 0.5 }}>
                    {formatTokens(m.pot)} in the pot from {m.bettors}{' '}
                    {m.bettors === 1 ? 'person' : 'people'}
                </Typography>
            </Box>

            {needsNumber && (
                <TextField
                    label="The actual number"
                    value={value}
                    onChange={(event) => {
                        setValue(event.target.value.replace(/[^\d.-]/g, ''));
                        setConfirming(false);
                    }}
                    type="tel"
                    inputMode="decimal"
                    autoFocus
                />
            )}

            {m.kind !== 'closest' && (
                <Stack spacing={1}>
                    {m.outcomes.map((outcome) => (
                        <Paper
                            key={outcome.id}
                            component="button"
                            type="button"
                            onClick={() => {
                                setPick(outcome.id);
                                setConfirming(false);
                            }}
                            sx={{
                                p: 1.75,
                                textAlign: 'left',
                                cursor: 'pointer',
                                font: 'inherit',
                                color: 'inherit',
                                width: '100%',
                                borderColor: pick === outcome.id ? colors.safety : colors.line,
                                borderWidth: pick === outcome.id ? 2 : 1,
                                bgcolor: pick === outcome.id ? colors.raised : colors.surface,
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                                {outcome.label}
                            </Typography>
                            <Typography sx={{ fontFamily: mono, fontSize: '0.75rem', color: colors.muted }}>
                                {outcome.staked === 0
                                    ? 'nobody backed this'
                                    : `${formatTokens(outcome.staked)} on it`}
                            </Typography>
                        </Paper>
                    ))}
                </Stack>
            )}

            {resolve.isError && <Alert severity="error">{getErrorMessage(resolve.error)}</Alert>}

            {resolve.isSuccess ? (
                <Stack spacing={2}>
                    <Alert severity="success">
                        {resolve.data.refunded
                            ? 'Everyone got their tokens back — there was no fair way to split it.'
                            : `${formatTokens(resolve.data.paid)} paid out to ${resolve.data.winners} ${
                                  resolve.data.winners === 1 ? 'person' : 'people'
                              }.`}
                    </Alert>
                    <Button variant="contained" size="large" onClick={() => navigate('/')}>
                        Back to the board
                    </Button>
                </Stack>
            ) : confirming ? (
                <Stack spacing={1.5}>
                    <Alert severity="warning">
                        This pays out {formatTokens(m.pot)} straight away. If it&rsquo;s wrong,
                        anyone who bet can dispute it.
                    </Alert>
                    <Button
                        variant="contained"
                        size="large"
                        color="primary"
                        disabled={resolve.isPending}
                        onClick={() =>
                            resolve.mutate({
                                marketId: m.id,
                                outcomeId: m.kind === 'closest' ? undefined : (pick ?? undefined),
                                resolvedValue: value.trim() === '' ? undefined : Number(value),
                            })
                        }
                    >
                        {resolve.isPending
                            ? 'Paying out…'
                            : needsNumber && m.kind === 'closest'
                              ? `Yes, it was ${value}`
                              : `Yes, ${chosen?.label ?? value}`}
                    </Button>
                    <Button color="inherit" onClick={() => setConfirming(false)}>
                        Wait, go back
                    </Button>
                </Stack>
            ) : (
                <Button
                    variant="contained"
                    size="large"
                    disabled={!ready}
                    onClick={() => setConfirming(true)}
                >
                    {ready
                        ? m.kind === 'closest'
                            ? `It was ${value}`
                            : `${chosen?.label} won`
                        : m.kind === 'closest'
                          ? 'Type the number'
                          : 'Pick what happened'}
                </Button>
            )}
        </Stack>
    );
}
